import {
  invoiceFileBaseName,
  UiInvoiceDraftRow,
  UiInvoiceSatField,
  UiInvoiceSatValidation,
} from './invoice-capture.types';
import { UiFileItem } from '../../molecules/file-upload/file-upload.types';

type CfdiParse = {
  folio: string;
  fecha: string;
  subtotal: string;
  descuento: string;
  metodoPago: string;
  formaPago: string;
  importe: string;
  sat: UiInvoiceSatValidation;
};

const PAYMENT_FORM_LABELS: Readonly<Record<string, string>> = {
  '01': 'Efectivo',
  '02': 'Cheque nominativo',
  '03': 'Transferencia electrónica de fondos',
  '04': 'Tarjeta de crédito',
  '05': 'Monedero electrónico',
  '06': 'Dinero electrónico',
  '08': 'Vales de despensa',
  '12': 'Dación en pago',
  '13': 'Pago por subrogación',
  '14': 'Pago por consignación',
  '15': 'Condonación',
  '17': 'Compensación',
  '23': 'Novación',
  '24': 'Confusión',
  '25': 'Remisión de deuda',
  '26': 'Prescripción o caducidad',
  '27': 'A satisfacción del acreedor',
  '28': 'Tarjeta de débito',
  '29': 'Tarjeta de servicios',
  '30': 'Aplicación de anticipos',
  '31': 'Intermediario pagos',
  '99': 'Por definir',
};

const CFDI_USE_LABELS: Readonly<Record<string, string>> = {
  G01: 'Adquisición de mercancías',
  G02: 'Devoluciones, descuentos o bonificaciones',
  G03: 'Gastos en general',
  I01: 'Construcciones',
  I02: 'Mobiliario y equipo de oficina por inversiones',
  I03: 'Equipo de transporte',
  I04: 'Equipo de cómputo y accesorios',
  I05: 'Dados, troqueles, moldes, matrices y herramental',
  I06: 'Comunicaciones telefónicas',
  I07: 'Comunicaciones satelitales',
  I08: 'Otra maquinaria y equipo',
  D01: 'Honorarios médicos, dentales y gastos hospitalarios',
  D02: 'Gastos médicos por incapacidad o discapacidad',
  D03: 'Gastos funerales',
  D04: 'Donativos',
  D05: 'Intereses reales efectivamente pagados por créditos hipotecarios',
  D06: 'Aportaciones voluntarias al SAR',
  D07: 'Primas por seguros de gastos médicos',
  D08: 'Gastos de transportación escolar obligatoria',
  D09: 'Depósitos en cuentas para el ahorro y planes de pensiones',
  D10: 'Pagos por servicios educativos',
  S01: 'Sin efectos fiscales',
  CP01: 'Pagos',
  CN01: 'Nómina',
  P01: 'Por definir',
};

function catalogValue(code: string, labels: Readonly<Record<string, string>>): string {
  if (!code) return '—';
  const label = labels[code];
  return label ? `${code} - ${label}` : code;
}

function formatCfdiDate(value: string): string {
  if (!value) return '—';
  const iso = value.slice(0, 10);
  const [year, month, day] = iso.split('-');
  return day && month && year ? `${day}/${month}/${year}` : '—';
}

function formatMoney(value: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function firstByLocalName(root: ParentNode, localName: string): Element | null {
  const nodes = (root as Document | Element).getElementsByTagName('*');
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].localName === localName) return nodes[i];
  }
  return null;
}

function allByLocalName(root: ParentNode, localName: string): Element[] {
  const nodes = (root as Document | Element).getElementsByTagName('*');
  const matches: Element[] = [];
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].localName === localName) matches.push(nodes[i]);
  }
  return matches;
}

function attr(el: Element | null, ...names: string[]): string {
  if (!el) return '';
  for (const name of names) {
    const value = el.getAttribute(name);
    if (value) return value;
  }
  return '';
}

function sumTax(
  impuestos: Element | null,
  nodeName: string,
  code: string,
): string {
  if (!impuestos) return '—';
  const nodes = allByLocalName(impuestos, nodeName).filter(
    el => attr(el, 'Impuesto') === code,
  );
  if (nodes.length === 0) return '—';
  const total = nodes.reduce(
    (sum, el) => sum + Number(attr(el, 'Importe') || 0),
    0,
  );
  return formatMoney(Number.isFinite(total) ? total : null);
}

function parseCfdi(xmlText: string): CfdiParse | null {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  if (doc.querySelector('parsererror')) return null;
  const comprobante = firstByLocalName(doc, 'Comprobante');
  if (!comprobante) return null;

  const emisor = firstByLocalName(comprobante, 'Emisor');
  const receptor = firstByLocalName(comprobante, 'Receptor');
  const impuestosNodes = allByLocalName(comprobante, 'Impuestos');
  const impuestos =
    impuestosNodes.find(el => el.parentElement?.localName === 'Comprobante') ??
    impuestosNodes[0] ??
    null;

  const folio = attr(comprobante, 'Folio') || attr(comprobante, 'Serie') || '—';
  const subtotalValue = attr(comprobante, 'SubTotal');
  const subtotal = subtotalValue ? Number(subtotalValue) : Number.NaN;
  const descuentoValue = attr(comprobante, 'Descuento');
  const descuento = descuentoValue ? Number(descuentoValue) : Number.NaN;
  const total = Number(attr(comprobante, 'Total'));

  return {
    folio,
    fecha: formatCfdiDate(attr(comprobante, 'Fecha')),
    subtotal: formatMoney(Number.isFinite(subtotal) ? subtotal : null),
    descuento: formatMoney(Number.isFinite(descuento) ? descuento : null),
    metodoPago: attr(comprobante, 'MetodoPago') || '—',
    formaPago: catalogValue(attr(comprobante, 'FormaPago'), PAYMENT_FORM_LABELS),
    importe: formatMoney(Number.isFinite(total) ? total : null),
    sat: {
      generales: [
        { label: 'RFC', value: attr(emisor, 'Rfc') || '—' },
        { label: 'Razón social', value: attr(emisor, 'Nombre') || '—' },
        {
          label: 'Uso de CFDI',
          value: catalogValue(attr(receptor, 'UsoCFDI', 'UsoCfdi'), CFDI_USE_LABELS),
        },
      ],
      impuestos: [
        {
          label: 'Impuesto al Valor Agregado (IVA)',
          value: sumTax(impuestos, 'Traslado', '002'),
        },
        { label: 'Retención ISR', value: sumTax(impuestos, 'Retencion', '001') },
        { label: 'Retención IVA', value: sumTax(impuestos, 'Retencion', '002') },
      ],
    },
  };
}

function satFieldValue(
  fields: readonly UiInvoiceSatField[],
  label: string,
): string {
  return fields.find(field => field.label === label)?.value ?? '—';
}

const FISCAL_UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function fiscalUuid(value: string): string {
  return value.match(FISCAL_UUID)?.[0].toLowerCase() ?? '';
}

export function toInvoiceDraft(
  item: UiFileItem,
  parsed: CfdiParse | null,
  pdf = '—',
  pdfFile: UiFileItem | null = null,
  namePaired = true,
): UiInvoiceDraftRow {
  const valid = parsed != null && namePaired;
  return {
    id: item.id,
    archivo: item.name,
    pdf,
    pdfFile,
    folio: parsed?.folio ?? '—',
    fecha: parsed?.fecha ?? '—',
    subtotal: parsed?.subtotal ?? '—',
    descuento: parsed?.descuento ?? '—',
    emisor: satFieldValue(parsed?.sat.generales ?? [], 'Razón social'),
    metodoPago: parsed?.metodoPago ?? '—',
    formaPago: parsed?.formaPago ?? '—',
    usoCfdi: satFieldValue(parsed?.sat.generales ?? [], 'Uso de CFDI'),
    iva: satFieldValue(
      parsed?.sat.impuestos ?? [],
      'Impuesto al Valor Agregado (IVA)',
    ),
    retencionIsr: satFieldValue(parsed?.sat.impuestos ?? [], 'Retención ISR'),
    retencionIva: satFieldValue(parsed?.sat.impuestos ?? [], 'Retención IVA'),
    importe: parsed?.importe ?? '—',
    estatus: valid ? 'Validado' : 'Error validación',
    namesMatch: namePaired,
    validacion: valid ? 'XML válido' : 'XML inválido',
    xml: item,
    sat: parsed?.sat ?? null,
  };
}

function pdfScore(pdf: UiFileItem, xmlBase: string, uuid: string): number {
  const name = pdf.name.toLowerCase();
  const base = invoiceFileBaseName(pdf);
  const compactName = name.replace(/-/g, '');
  const compactUuid = uuid.replace(/-/g, '');
  if (base === xmlBase) return 100;
  if (uuid && (base === uuid || name.includes(uuid) || compactName.includes(compactUuid))) return 90;
  if (xmlBase.length >= 8 && (base.includes(xmlBase) || name.includes(xmlBase))) return 80;
  if (base.length >= 8 && xmlBase.includes(base)) return 70;
  return 0;
}

function pickPdfByName(
  xml: UiFileItem,
  pdfFiles: readonly UiFileItem[],
  used: Set<string>,
): UiFileItem | null {
  const xmlBase = invoiceFileBaseName(xml);
  return (
    pdfFiles.find(pdf => !used.has(pdf.id) && invoiceFileBaseName(pdf) === xmlBase) ??
    null
  );
}

function unpairedPdfDraft(pdf: UiFileItem): UiInvoiceDraftRow {
  return {
    id: pdf.id,
    archivo: '—',
    pdf: pdf.name,
    pdfFile: pdf,
    folio: '—',
    fecha: '—',
    subtotal: '—',
    descuento: '—',
    emisor: '—',
    metodoPago: '—',
    formaPago: '—',
    usoCfdi: '—',
    iva: '—',
    retencionIsr: '—',
    retencionIva: '—',
    importe: '—',
    estatus: 'Error validación',
    namesMatch: false,
    validacion: 'XML inválido',
    xml: null,
    sat: null,
  };
}

function pickPdf(
  xml: UiFileItem,
  pdfFiles: readonly UiFileItem[],
  uuid: string,
  used: Set<string>,
): UiFileItem | null {
  const xmlBase = invoiceFileBaseName(xml);
  let best: UiFileItem | null = null;
  let bestScore = 0;
  for (const pdf of pdfFiles) {
    if (used.has(pdf.id)) continue;
    const score = pdfScore(pdf, xmlBase, uuid);
    if (score > bestScore) {
      best = pdf;
      bestScore = score;
    }
  }
  return best;
}

export async function draftsFromInvoiceFiles(
  files: readonly UiFileItem[],
  options?: { pairByFileName?: boolean },
): Promise<UiInvoiceDraftRow[]> {
  const xmlFiles = files.filter(item => item.extension === 'xml');
  const pdfFiles = files.filter(item => item.extension === 'pdf');
  const drafts = await Promise.all(
    xmlFiles.map(async item => {
      try {
        const text = await item.file.text();
        return {
          xml: item,
          parsed: parseCfdi(text),
          uuid: fiscalUuid(text) || fiscalUuid(item.name),
        };
      } catch {
        return { xml: item, parsed: null, uuid: fiscalUuid(item.name) };
      }
    }),
  );

  const used = new Set<string>();
  const pairByFileName = options?.pairByFileName === true;
  const rows = drafts.map(draft => {
    const pdf = pairByFileName
      ? pickPdfByName(draft.xml, pdfFiles, used)
      : pickPdf(draft.xml, pdfFiles, draft.uuid, used);
    if (pdf) used.add(pdf.id);
    return toInvoiceDraft(
      draft.xml,
      draft.parsed,
      pdf?.name ?? '—',
      pdf,
      pairByFileName ? pdf != null : true,
    );
  });
  if (!pairByFileName) return rows;
  const unpaired = pdfFiles
    .filter(pdf => !used.has(pdf.id))
    .map(pdf => unpairedPdfDraft(pdf));
  return [...rows, ...unpaired];
}
